import { VerificationType } from "@taskfortress/core/src/api/types"
import { useField, useForm } from "@taskfortress/core/src/form"
import { validateVerificationCode } from "@taskfortress/core/src/form/validation"
import { useCallback, useState } from "react"
import { useAuth } from "../auth"
import { BaseScreen, Button, Text, TextLink } from "../components"
import { AuthForm, AuthLayout } from "../components/auth"
import { FormError, TextInput } from "../components/form"

export const VerifyCodeScreen = ({ route, navigation }: any) => {
  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState<string>("")
  const { verifyCode, signup } = useAuth()

  // Get params passed from previous screen
  const { email, password, masterKey, verificationToken, verificationType } =
    route.params || {}

  // Form fields
  const codeField = useField({
    name: "code",
    initialValue: "",
    validate: validateVerificationCode,
  })

  // Form management
  const form = useForm({
    fields: [codeField],
  })

  const handleVerify = useCallback(async () => {
    if (!form.valid) {
      setFormError("Please enter a valid verification code")
      return
    }

    setIsLoading(true)
    setFormError("")
    try {
      // Verify the code
      const success = await verifyCode(
        email,
        verificationToken,
        codeField.value!,
        verificationType,
      )

      if (!success) {
        setFormError("Invalid code. Please try again.")
        setIsLoading(false)
        return
      }

      // Code verified, now complete the flow based on verificationType
      if (verificationType === VerificationType.SIGNUP) {
        // Complete signup process
        const signupSuccess = await signup(
          email,
          password,
          masterKey,
          verificationToken,
          codeField.value!,
        )
        if (signupSuccess) {
          // Successfully signed up and logged in
          return // Auth provider will redirect to main app
        } else {
          setFormError(
            "Verification succeeded but signup failed. Please try again.",
          )
        }
      } else if (verificationType === VerificationType.RESET) {
        // Navigate to reset password screen
        navigation.navigate("ResetPassword", {
          email,
          verificationToken,
          code: codeField.value,
        })
        return
      }
    } catch (error) {
      console.error("Verification error:", error)
      setFormError("An error occurred during verification.")
    } finally {
      setIsLoading(false)
    }
  }, [
    form.valid,
    codeField.value,
    email,
    verificationToken,
    verificationType,
    password,
    masterKey,
    verifyCode,
    signup,
    navigation,
  ])

  return (
    <BaseScreen>
      <AuthLayout>
        <Text className="text-2xl font-bold mb-6 text-center">
          Email verification
        </Text>

        <AuthForm onSubmit={handleVerify}>
          <TextInput
            name="code"
            label="Enter verification code sent to your email"
            placeholder="000000"
            value={codeField.value}
            handleChange={codeField.handleChange}
            disabled={isLoading}
            onEnterPress={handleVerify}
          />

          <FormError error={formError} />

          <Button
            onPress={handleVerify}
            disabled={isLoading || !form.valid}
            isLoading={isLoading}
          >
            Verify
          </Button>

          <TextLink
            onPress={() => navigation.goBack()}
            disabled={isLoading}
            className="text-center mx-auto"
          >
            Go Back
          </TextLink>
        </AuthForm>
      </AuthLayout>
    </BaseScreen>
  )
}
